import axios from "axios";

// Use relative /api — both preview and deployed environments serve
// frontend + backend on the same domain via kubernetes ingress.
// Absolute URLs cause cross-origin CORS issues on custom domains.
const api = axios.create({
  baseURL: "/api",
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Send cookies
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('session_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 🔁 Auto-retry on transient infra errors (5xx / network / timeout) so a
// briefly-sleeping production backend doesn't surface to the user as a
// permanent failure. Retries up to 2 times with exponential backoff.
const RETRY_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524]);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const status = error.response?.status;
    const isNetwork = !error.response;                // server unreachable / DNS / CORS preflight fail
    const isServerErr = status && RETRY_STATUSES.has(status);
    const isRetryable = (isNetwork || isServerErr) && config && !config._retry_count;

    if (isRetryable) {
      config._retry_count = (config._retry_count || 0) + 1;
      // Up to 2 retries with backoff: 1.5s, 3s
      while (config._retry_count <= 2) {
        const delay = 1500 * config._retry_count;
        await new Promise(r => setTimeout(r, delay));
        try {
          return await api.request(config);
        } catch (retryErr) {
          const rStatus = retryErr.response?.status;
          const retryable = !retryErr.response || (rStatus && RETRY_STATUSES.has(rStatus));
          if (!retryable || config._retry_count >= 2) {
            // Final failure — annotate so callers can show real reason
            retryErr.isBackendDown = true;
            console.error("API down after retries:", retryErr.message || retryErr);
            return Promise.reject(retryErr);
          }
          config._retry_count += 1;
        }
      }
    }

    console.error("API Error:", error.response?.data || error.message);
    // 🛡️ 401 handling: kiosk-friendly.
    // Only redirect if the 401 came from an *auth* endpoint (means our session
    // token was truly rejected). A 401 on any other endpoint (e.g., a
    // temporarily-flaky data endpoint that requires auth) should NOT nuke the
    // kiosk session — that caused TVs to drop back to /login every ~10 min
    // whenever the backend had a hiccup.
    if (status === 401) {
      const url = (config?.url || "").toLowerCase();
      const isAuthEndpoint = url.includes("/auth/me") || url.includes("/auth/login") || url.includes("/auth/session");
      if (isAuthEndpoint) {
        localStorage.removeItem('session_token');
        // Don't force-redirect if we're already on /login or on /tv (kiosk).
        // Kiosk should show a stale-cache banner, not kick to login.
        const onKiosk = typeof window !== "undefined" && window.location.pathname.startsWith("/tv");
        if (!onKiosk && !window.location.pathname.startsWith("/login")) {
          window.location.href = '/login';
        }
      } else {
        console.warn(`401 on non-auth endpoint ${url} — keeping session, likely transient.`);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
